import { ApolloClient } from '@apollo/client';

import { TypeaheadSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { InsurancePolicyKind } from '@/types/schema';

import {
  InferInsurancePolicyKindDocument,
  InferInsurancePolicyKindQuery,
  InferNameMatchDocument,
  InferNameMatchQuery,
} from './graphql/IntegrationInference.generated';

export interface InferNameMatchCandidate {
  id: string;
  name: string;
}

interface InferenceOption {
  display?: string;
  value?: string;
}

export function getInferNameMatchCandidatesFromOptions(
  options: (
    | Pick<TypeaheadSelectInputOption<string>, 'display' | 'value'>
    | InferenceOption
  )[]
): InferNameMatchCandidate[] {
  return options.flatMap(({ display, value }) => {
    const name = display?.trim();
    if (!value || !name) return [];

    return [{ id: value, name }];
  });
}

export async function inferNameMatch({
  client,
  name,
  candidates,
}: {
  client: ApolloClient<object>;
  name: string | null | undefined;
  candidates: InferNameMatchCandidate[];
}): Promise<string | null> {
  const trimmedName = name?.trim();
  if (!trimmedName || candidates.length === 0) return null;

  const { data } = await client.query<InferNameMatchQuery>({
    query: InferNameMatchDocument,
    variables: { input: { name: trimmedName, candidates } },
    fetchPolicy: 'no-cache',
  });
  return data.inferNameMatch ?? null;
}

export async function inferInsurancePolicyKind({
  client,
  input,
}: {
  client: ApolloClient<object>;
  input: string | null | undefined;
}): Promise<InsurancePolicyKind | null> {
  const trimmedInput = input?.trim();
  if (!trimmedInput) return null;

  const { data } = await client.query<InferInsurancePolicyKindQuery>({
    query: InferInsurancePolicyKindDocument,
    variables: { input: trimmedInput },
    fetchPolicy: 'no-cache',
  });
  return data.inferInsurancePolicyKind ?? null;
}
